import apiUtil from '~/utils/apiUtils'

const { get, post, del } = apiUtil()

interface OIDCEmailCheckResponse {
  oidcEnabled: boolean
  orgId?: string
  enforceSSO: boolean
}

interface OIDCEnabledResponse {
  enabled: boolean
  enforceSSO: boolean
}

interface OIDCLoginInitResponse {
  authorizationUrl: string
  state: string
}

interface OIDCConfigInput {
  clientId: string
  clientSecret: string
  authorizationUrl: string
  tokenUrl: string
  jwksUri: string
  userinfoUrl?: string
  endSessionUrl?: string
  enforceSSO: boolean
  apiToken?: string
  oktaDomain?: string
  syncEmployeeCount: boolean
  allowedEmailDomains: string[]
}

interface OIDCConfigResponse {
  id: string
  orgId: string
  providerName: string
  clientId: string
  authorizationUrl: string
  tokenUrl: string
  jwksUri: string
  userinfoUrl?: string
  endSessionUrl?: string
  enabled: boolean
  enforceSSO: boolean
  oktaDomain?: string
  syncEmployeeCount: boolean
  allowedEmailDomains: string[]
  hasApiToken: boolean
  createdAt: string
  updatedAt: string
}

interface TestConfigResponse {
  success: boolean
  message: string
}

interface SyncEmployeeCountResponse {
  success: boolean
  employeeCount: number
  message: string
}

class OIDCApi {
  async checkByEmail(email: string): Promise<OIDCEmailCheckResponse> {
    const response = await get(`/oidc/check-by-email?email=${encodeURIComponent(email)}`, {}, { isPublic: true })
    return await response.json()
  }

  async checkOIDCEnabled(orgId: string): Promise<OIDCEnabledResponse> {
    const response = await get(`/oidc/org/${orgId}/enabled`, {}, { isPublic: true })
    return await response.json()
  }

  async initiateOIDCLogin(orgId: string, redirectUri?: string): Promise<OIDCLoginInitResponse> {
    const url = redirectUri ? `/oidc/login/init?redirect_uri=${encodeURIComponent(redirectUri)}` : '/oidc/login/init'
    const response = await post(url, { orgId }, { isPublic: true })
    return await response.json()
  }

  // Returns null when no config exists (404). Other failures throw with the
  // backend's message — a 403/500 must never be mistaken for "not configured".
  async getOIDCConfig(): Promise<OIDCConfigResponse | null> {
    const response = await get('/oidc/config')
    const data = await response.json().catch(() => ({}))
    if (response.status === 404) return null
    if (!response.ok) {
      throw Object.assign(new Error(data.error || `HTTP error! status: ${response.status}`), {
        status: response.status,
      })
    }
    return data
  }

  async saveOIDCConfig(config: OIDCConfigInput): Promise<any> {
    const response = await post('/oidc/config', config)
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw Object.assign(new Error(data.error || `HTTP error! status: ${response.status}`), {
        status: response.status,
      })
    }
    return data
  }

  async deleteOIDCConfig(): Promise<any> {
    const response = await del('/oidc/config')
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw Object.assign(new Error(data.error || `HTTP error! status: ${response.status}`), {
        status: response.status,
      })
    }
    return data
  }

  async testOIDCConfig(config: OIDCConfigInput): Promise<TestConfigResponse> {
    const response = await post('/oidc/config/test', config)
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw Object.assign(new Error(data.error || `HTTP error! status: ${response.status}`), {
        status: response.status,
      })
    }
    return data
  }

  /**
   * Triggers advanced synchronization from Okta:
   * - Organization employee count
   * - User profile data (department, location, manager)
   * - Team creation/mapping from Okta groups
   */
  async syncEmployeeCount(): Promise<SyncEmployeeCountResponse> {
    const response = await post('/oidc/config/sync-employee-count')
    return await response.json()
  }
}

export default new OIDCApi()

